import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Typography, TextField, Paper, Chip, IconButton,
    FormControl,
    FormLabel,
    Select,
    MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageIcon from '@mui/icons-material/Image';
import { ApiServiceContext } from '../../../../api/ApiService.context';
import { QuoteExperienceService } from '../../../../api/quote-experience/QuoteExperienceService.class';
import { QuoteExperienceState, QEImage } from '../../QuoteExperienceQuickSetup.page';
import { blobToDataURI } from '../../QuoteExperience.helpers';
import { ALLOWED_IMAGE_FILE_MIME_TYPES, generateId, sanitizeName } from '../../utils/fileUtils';

type ManagedImage = QEImage & {
    updated?: boolean;
};

const BASE_IMAGE_PATHS = ['images', 'logos', 'favicon', 'icons'] as const;
export type BaseImagePath = typeof BASE_IMAGE_PATHS[number];

const SHOW_BASE_PATH_SELECTOR: boolean = true;

const getFileExtension = (file: File): string => {
    const nameExt = file.name.split('.').pop()?.toLowerCase();
    if (nameExt && nameExt !== file.name.toLowerCase()) return nameExt;
    const mimeToExt: Record<string, string> = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
        'image/webp': 'webp',
        'image/x-icon': 'ico',
        'application/json': 'json'
    };
    return mimeToExt[file.type] || 'png';
};

const ensureExtension = (name: string, file: File): string => {
    if (/\.[a-zA-Z0-9]+$/.test(name)) return name;
    return `${name}.${getFileExtension(file)}`;
};

export const ImageManagerPanel: FC<{
    bundleGuid: string | undefined;
    current: Partial<QuoteExperienceState>;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    setIsLoading: (key: string, value: boolean) => void;
}> = ({ bundleGuid, current, updateData, setIsLoading }) => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { QuoteExperienceService } = getApiServiceCollections() as {
        QuoteExperienceService: QuoteExperienceService;
    };

    const [existingImages, setExistingImages] = useState<ManagedImage[]>([]);
    const [loadError, setLoadError] = useState<string>('');
    const [baseImagePath, setBaseImagePath] = useState<BaseImagePath>('images');
    const [assetsLoaded, setAssetsLoaded] = useState<'NOT STARTED' | 'LOADING' | 'DONE'>('NOT STARTED');

    const pendingImages = useMemo(() => {
        const paths = new Set(existingImages.map(image => image.path));
        return (current[baseImagePath] || []).filter(image => {
            return !paths.has(image.path)
        })
    }, [current, baseImagePath, existingImages]);

    useEffect(() => {
        if (!bundleGuid || assetsLoaded !== 'NOT STARTED') return;
        setAssetsLoaded('LOADING');
        const loadExistingImages = async () => {
            setIsLoading('ImageManager', true);
            setLoadError('');
            try {
                const listFiles = QuoteExperienceService.listAssetFiles;
                const resp = await listFiles({
                    bundleGuid,
                    folderPath: `assets/${baseImagePath}`
                });
                const fileNames: string[] = (resp.jsonBody ?? []).map((fullPath: string) => {
                    const parts = fullPath.split('/');
                    return parts[parts.length - 1];
                });

                const imageData = await Promise.all(
                    fileNames.map(async (name) => {
                        try {
                            const blob = await QuoteExperienceService.loadImage(
                                bundleGuid, `assets/${baseImagePath}/${name}`
                            );
                            let previewUrl: string | undefined;
                            if (blob) {
                                const dataUri = await blobToDataURI(blob);
                                previewUrl = dataUri as string;
                            }
                            return { 
                                id: name, 
                                name, 
                                previewUrl, 
                                file: blob,
                                path: `assets/${baseImagePath}/${name}`
                            };
                        } catch {
                            return { 
                                id: name, 
                                name, 
                                previewUrl: undefined, 
                                file: undefined,
                                path: `assets/${baseImagePath}/${name}`
                            };
                        }
                    })
                );
                setExistingImages(imageData);
            } catch (err) {
                setLoadError('Failed to load existing images');
                console.error('Failed to load images', err);
            } finally {
                setIsLoading('ImageManager', false);
                setAssetsLoaded('DONE');
            }
        };

        loadExistingImages();
        return () => { };
    }, [assetsLoaded, baseImagePath, QuoteExperienceService, bundleGuid, setIsLoading]);

    const handleAddImage = useCallback(() => {
        const newImage: QEImage = {
            id: generateId(),
            name: '',
            file: undefined,
            previewUrl: undefined,
            error: null,
            path: undefined,
        };
        updateData(`/${baseImagePath}`, [...pendingImages, newImage]);
    }, [pendingImages, baseImagePath, updateData]);

    const handleUpdateImage = useCallback((id: string, patch: Partial<QEImage>) => {
        updateData(`/${baseImagePath}`, (currentImages: any) => {
            const images: QEImage[] = currentImages || [];
            const idx = images.findIndex((img: QEImage) => img.id === id);
            if (idx === -1) return images;
            const updated = { ...images[idx], ...patch };
            if (updated.name && updated.file) {
                const cleanName = sanitizeName(updated.name);
                const finalName = updated.file instanceof File
                    ? ensureExtension(cleanName, updated.file)
                    : cleanName;
                updated.path = `assets/${baseImagePath}/${finalName}`;
            }
            const newImages = [...images];
            newImages[idx] = updated;
            return newImages;
        });
    }, [baseImagePath, updateData]);

    const handleRemoveImage = useCallback((id: string) => {
        const img = pendingImages.find(i => i.id === id);
        if (img?.previewUrl && typeof img.previewUrl === 'string'
            && img.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(img.previewUrl);
        }
        updateData(`/${baseImagePath}`, pendingImages.filter(i => i.id !== id));
    }, [pendingImages, baseImagePath, updateData]);

    const handleUpdateExistingImage = useCallback((file: File, idx: number, existing: ManagedImage) => {
        const previewUrl = URL.createObjectURL(file);
        const { updated, ...newImage } = existing;
        newImage.previewUrl = previewUrl;
        newImage.file = file;
        setExistingImages((images: ManagedImage[]) => {
            const newImages = [...images];
            newImages[idx] = newImage;
            newImages[idx].updated = true;
            return newImages;
        })
        updateData(`/${baseImagePath}`, (currentImages: any) => {
            return [...currentImages, newImage];
        });
    }, [baseImagePath, updateData]);

    const handleFileSelect = useCallback((id: string, file: File) => {
        const previewUrl = URL.createObjectURL(file);
        updateData(`/${baseImagePath}`, (currentImages: any) => {
            const images: QEImage[] = currentImages || [];
            const idx = images.findIndex((img: QEImage) => img.id === id);
            if (idx === -1) return images;
            const pendingImage = images[idx];
            const updated: QEImage = {
                ...(pendingImage || {}),
                id: pendingImage?.id,
                file,
                previewUrl,
                error: null,
                name: pendingImage?.name,
            };
            if (updated.file) {
                let nameToUse = pendingImage.name;
                if (!nameToUse && updated.file instanceof File) {
                    nameToUse = updated.file.name;
                }
                if (!nameToUse) {
                    nameToUse = 'New Image';
                }
                const cleanName = sanitizeName(nameToUse);
                const finalName = updated.file instanceof File
                    ? ensureExtension(cleanName, updated.file)
                    : cleanName;
                updated.name = finalName;
                updated.path = `assets/${baseImagePath}/${finalName}`;
                updated.id = updated.path;
            }
            const newImages = [...images];
            if (idx === -1) {
                newImages.push(updated);
            } else {
                newImages[idx] = updated;
            }
            return newImages;
        });
    }, [baseImagePath, updateData]);

    const getBasePathSelector = useCallback(() => {
        if (!SHOW_BASE_PATH_SELECTOR) {
            return null;
        }
        return (
            <Box sx={{ position: "relative" }} >
                <FormControl
                    fullWidth
                    sx={{ width: "40%" }}
                    size="medium"
                >
                    <FormLabel htmlFor="select-asset-path" sx={{ fontWeight: 500, display: 'block' }}>Asset Path</FormLabel>
                    <Select
                        id="select-asset-path"
                        label="Asset Path"
                        value={baseImagePath}
                        onChange={event => {
                            setBaseImagePath(event.target.value as BaseImagePath);
                            setAssetsLoaded('NOT STARTED');
                        }}
                    >
                        {
                            BASE_IMAGE_PATHS.map(path => (
                                <MenuItem value={path} >{path}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            </Box>
        )
    }, [baseImagePath]);

    return (
        <Box>
            {getBasePathSelector()}
            {loadError && (
                <Typography color="error" sx={{ mb: 2 }}>{loadError}</Typography>
            )}

            {existingImages.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Uploaded files
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Files currently available in /assets/{baseImagePath}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {existingImages.map((item, idx) => (
                            <Paper
                                key={item.id}
                                variant="outlined"
                                sx={{ p: 2, position: 'relative' }}
                            >
                                <Chip
                                    label={item.updated ? 'Pending' : 'Uploaded'}
                                    size="small"
                                    color={item.updated ? 'warning' : 'success'}
                                    variant="outlined"
                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pr: 10 }}>
                                    <Box
                                        sx={{
                                            width: 80, height: 80,
                                            border: '1px dashed', borderColor: 'divider',
                                            borderRadius: 1,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0, backgroundColor: 'grey.50'
                                        }}
                                    >
                                        {item.previewUrl ? (
                                            <img
                                                src={item.previewUrl as string}
                                                alt={item.name}
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <ImageIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ mb: 1.5 }}>
                                            <input
                                                type="file"
                                                accept={ALLOWED_IMAGE_FILE_MIME_TYPES.join(',')}
                                                id={`file-input-${item.id}`}
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleUpdateExistingImage(file, idx, item);
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component="label"
                                                htmlFor={`file-input-${item.id}`}
                                                startIcon={<AddPhotoAlternateIcon />}
                                            >
                                                {item.file ? 'Change Image' : 'Select Image'}
                                            </Button>
                                            {item.file && (
                                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                    {(item.file as File).name}
                                                </Typography>
                                            )}
                                        </Box>
                                        <TextField
                                            disabled
                                            size="small"
                                            label="Image name"
                                            value={item.name}
                                            fullWidth
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    New Images
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    {pendingImages.map((item) => {
                        const detectedExt = item.file instanceof File
                            ? getFileExtension(item.file)
                            : null;
                        const nameHasExtension = item.name ? /\.[a-zA-Z0-9]+$/.test(item.name) : false;

                        return (
                            <Paper
                                key={item.id}
                                variant="outlined"
                                sx={{
                                    p: 2, position: 'relative',
                                    borderColor: item.error ? 'error.main' : 'divider'
                                }}
                            >
                                <IconButton
                                    aria-label="Remove image"
                                    onClick={() => handleRemoveImage(item.id)}
                                    size="small"
                                    sx={{ position: 'absolute', top: 8, right: 8, color: 'error.main' }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                                <Chip
                                    label="Pending"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ position: 'absolute', top: 8, right: 48 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pr: 10 }}>
                                    <Box
                                        sx={{
                                            width: 80, height: 80,
                                            border: '1px dashed', borderColor: 'divider',
                                            borderRadius: 1,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0, backgroundColor: 'grey.50'
                                        }}
                                    >
                                        {item.previewUrl ? (
                                            <img
                                                src={item.previewUrl as string}
                                                alt="Preview"
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <ImageIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ mb: 1.5 }}>
                                            <input
                                                type="file"
                                                accept={ALLOWED_IMAGE_FILE_MIME_TYPES.join(',')}
                                                id={`file-input-${item.id}`}
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleFileSelect(item.id, file);
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component="label"
                                                htmlFor={`file-input-${item.id}`}
                                                startIcon={<AddPhotoAlternateIcon />}
                                            >
                                                {item.file ? 'Change Image' : 'Select Image'}
                                            </Button>
                                            {item.file && (
                                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                    {(item.file as File).name}
                                                </Typography>
                                            )}
                                        </Box>
                                        <TextField
                                            size="small"
                                            label="Image name"
                                            placeholder="e.g. hero-banner"
                                            value={item.name}
                                            onChange={(e) => handleUpdateImage(item.id, { name: e.target.value, error: null })}
                                            error={!!item.error}
                                            helperText={
                                                item.error ||
                                                (detectedExt && !nameHasExtension
                                                    ? `Extension will be auto-added: .${detectedExt}`
                                                    : 'Enter a name for your image')
                                            }
                                            fullWidth
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>

                <Button
                    variant="outlined"
                    onClick={handleAddImage}
                    startIcon={<AddPhotoAlternateIcon />}
                >
                    Add Image
                </Button>

                {pendingImages.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Images will be uploaded when you click Save
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
